/**
 * CreateSaleOpportunityDialog
 * Opens from the Horse profile header when the user selects "Sale Opportunity"
 * or clicks the "+" button while status is already "Sale Opportunity".
 * The horse is pre-selected and shown read-only; the user only needs to pick a sale.
 *
 * Props:
 *   onSuccess — async function called after a successful create, before navigation.
 *               Used by HorseDetailPage to persist the "Sale Opportunity" status
 *               when the dialog was opened from the dropdown (deferred save).
 *               Pass null when opened from the "+" button (status already saved).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const CreateSaleOpportunityDialog = ({ open, onClose, horse, onSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sales, setSales] = useState([]);
  const [opportunities, setOpportunities] = useState([]);

  // Form state
  const [selectedSale, setSelectedSale] = useState('');
  const [soldAs, setSoldAs] = useState('');
  const [propertyLine, setPropertyLine] = useState('');
  const [saleEntryNotes, setSaleEntryNotes] = useState('');
  const [entryStatus, setEntryStatus] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedSale('');
    setSoldAs('');
    setPropertyLine('');
    setSaleEntryNotes('');
    setEntryStatus('');
    fetchData();
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [salesRes, oppsRes] = await Promise.all([
        api.get('/tenant-sales'),
        api.get('/sale-opportunities'),
      ]);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const openSales = (salesRes.data || []).filter(sale => {
        const rawEnd = sale.end_date || sale.sale_end_date;
        if (!rawEnd) return true;
        const endDate = new Date(rawEnd);
        if (isNaN(endDate.getTime())) return true;
        endDate.setHours(23, 59, 59, 999);
        return endDate >= today;
      }).sort((a, b) => {
        const aDate = new Date(a.start_date || a.sale_start_date || '9999-12-31');
        const bDate = new Date(b.start_date || b.sale_start_date || '9999-12-31');
        return aDate - bDate || (a.sale_name || '').localeCompare(b.sale_name || '');
      });

      setSales(openSales);
      setOpportunities(oppsRes.data || []);
    } catch {
      toast.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  const selectedSaleObj = sales.find(s => s.id === selectedSale);
  const isFasig = selectedSaleObj?.sale_company?.toLowerCase().includes('fasig');
  const isKeeneland = selectedSaleObj?.sale_company?.toLowerCase().includes('keeneland');
  const alreadyEntered = selectedSale && horse &&
    opportunities.some(o => o.horse_id === horse.id && o.sale_id === selectedSale);

  const handleCreate = async () => {
    if (!selectedSale) {
      toast.error('Please select a sale');
      return;
    }
    if (alreadyEntered) {
      toast.error('This horse is already entered in the selected sale');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sale_id: selectedSale,
        horse_id: horse.id,
        ...(soldAs ? { sold_as: soldAs } : {}),
        ...(propertyLine ? { property_line: propertyLine } : {}),
        ...(saleEntryNotes ? { sale_entry_notes: saleEntryNotes } : {}),
        ...(entryStatus && entryStatus !== '__none__' ? { entry_status: entryStatus } : {}),
      };
      const res = await api.post('/sale-opportunities', payload);
      toast.success('Sale opportunity created');
      // Save horse status first (deferred save from dropdown selection)
      if (onSuccess) await onSuccess();
      onClose();
      navigate(`/sales/opportunities/${res.data.id}/select-deliverables`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create opportunity');
    } finally {
      setSaving(false);
    }
  };

  const horseName = horse?.system_name || horse?.name || 'Horse';

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-opp-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-semibold">Create Sale Opportunity</DialogTitle>
          <p className="text-sm text-muted-foreground">Select a sale and horse to create an opportunity</p>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Sale selector */}
            <div className="space-y-2">
              <Label>Select Sale *</Label>
              <Select value={selectedSale} onValueChange={setSelectedSale} data-testid="opp-sale-select">
                <SelectTrigger>
                  <SelectValue placeholder="Choose a sale..." />
                </SelectTrigger>
                <SelectContent>
                  {sales.length === 0 ? (
                    <SelectItem value="none" disabled>No active sales. Add a sale to Active Sales first.</SelectItem>
                  ) : (
                    sales.map(sale => {
                      const startDate = sale.start_date || sale.sale_start_date;
                      return (
                        <SelectItem key={sale.id} value={sale.id}>
                          {sale.sale_name} ({startDate ? new Date(startDate).toLocaleDateString() : 'Date TBD'})
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {sales.length === 0 && (
                <p className="text-xs text-amber-600">No active sales found. Add a sale to your Active Sales first.</p>
              )}
              {alreadyEntered && (
                <p className="text-xs text-red-600">This horse is already entered in the selected sale.</p>
              )}
            </div>

            {/* Horse — pre-selected, read-only */}
            <div className="space-y-2">
              <Label>Horse</Label>
              <div className="flex items-center h-10 px-3 rounded-md border bg-muted/40 text-sm font-medium" data-testid="opp-horse-readonly">
                {horseName}
              </div>
            </div>

            {/* Additional fields — shown once a sale is selected */}
            {selectedSale && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label>To Be Sold As</Label>
                  <Select value={soldAs} onValueChange={setSoldAs}>
                    <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weanling">Weanling</SelectItem>
                      <SelectItem value="yearling">Yearling</SelectItem>
                      <SelectItem value="2yo">2YO</SelectItem>
                      <SelectItem value="hora">Horses of Racing Age (HORA)</SelectItem>
                      <SelectItem value="broodmare">Broodmare</SelectItem>
                      <SelectItem value="broodmare_prospect">Broodmare Prospect</SelectItem>
                      <SelectItem value="racing_broodmare_prospect">Racing/Broodmare Prospect</SelectItem>
                      <SelectItem value="racing_prospect">Racing Prospect</SelectItem>
                      <SelectItem value="stallion">Stallion</SelectItem>
                      <SelectItem value="stallion_prospect">Stallion Prospect</SelectItem>
                      <SelectItem value="racing_stallion_prospect">Racing/Stallion Prospect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>
                    Property Line{' '}
                    {isKeeneland && <span className="text-muted-foreground text-xs">(recommended for Keeneland)</span>}
                  </Label>
                  <Input
                    value={propertyLine}
                    onChange={e => setPropertyLine(e.target.value)}
                    placeholder="Identifies the seller or legal owner of the horse at the time of the sale"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Entry Status</Label>
                  <Select value={entryStatus || '__none__'} onValueChange={v => setEntryStatus(v === '__none__' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="Select status (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {isFasig ? (
                        <>
                          <SelectItem value="nominated">Nominated</SelectItem>
                          <SelectItem value="still_under_consideration">Still Under Consideration</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="recommended_another_sale">Recommended Another Sale</SelectItem>
                          <SelectItem value="withdrawn">Withdrawn</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="entered">Entered</SelectItem>
                          <SelectItem value="withdrawn">Withdrawn</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Sale Entry Notes</Label>
                  <Textarea
                    value={saleEntryNotes}
                    onChange={e => setSaleEntryNotes(e.target.value)}
                    placeholder="Additional notes about this sale entry..."
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="opp-dialog-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!selectedSale || saving || alreadyEntered || loading}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="opp-dialog-create"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
            ) : (
              <>Create Opportunity <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSaleOpportunityDialog;
